import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, PlayArrow, Stop } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuctionForm from '../components/AuctionForm';
import api from '../services/api';

const AuctionAdmin = () => {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/auctions');
      setAuctions(response.data.auctions || []);
    } catch (err) {
      setError('Failed to load auctions');
      console.error('Error fetching auctions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedAuction(null);
    setDialogOpen(true);
  };

  const handleEdit = (auction) => {
    setSelectedAuction(auction);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this auction?')) {
      return;
    }

    try {
      await api.delete(`/auctions/${id}`);
      fetchAuctions();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete auction');
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (selectedAuction) {
        await api.put(`/auctions/${selectedAuction.id}`, data);
      } else {
        await api.post('/auctions', data);
      }

      setDialogOpen(false);
      fetchAuctions();
    } catch (err) {
      throw err;
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/auctions/${id}`, { status: newStatus });
      fetchAuctions();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'ended':
        return 'default';
      case 'draft':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box py={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight="bold">
            Auction Management
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Create Auction
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Start Price</TableCell>
                  <TableCell>Current Bid</TableCell>
                  <TableCell>Bids</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auctions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" py={4}>
                        No auctions created yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auctions.map((auction) => (
                    <TableRow key={auction.id}>
                      <TableCell>{auction.item?.name || 'Unknown Item'}</TableCell>
                      <TableCell>{formatPrice(auction.start_price)}</TableCell>
                      <TableCell>{formatPrice(auction.current_bid || auction.start_price)}</TableCell>
                      <TableCell>{auction.bid_count || 0}</TableCell>
                      <TableCell>
                        <Chip label={auction.status} color={getStatusColor(auction.status)} size="small" />
                      </TableCell>
                      <TableCell>{formatDate(auction.start_time)}</TableCell>
                      <TableCell>{formatDate(auction.end_time)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/auctions/${auction.id}`)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(auction)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        {auction.status === 'draft' && (
                          <Tooltip title="Activate">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleStatusChange(auction.id, 'active')}
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        )}

                        {auction.status === 'active' && (
                          <Tooltip title="End Auction">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleStatusChange(auction.id, 'ended')}
                            >
                              <Stop />
                            </IconButton>
                          </Tooltip>
                        )}

                        {auction.bid_count === 0 && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(auction.id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{selectedAuction ? 'Edit Auction' : 'Create Auction'}</DialogTitle>
          <DialogContent>
            <Box pt={1}>
              <AuctionForm
                auction={selectedAuction}
                onSubmit={handleSubmit}
                onCancel={() => setDialogOpen(false)}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AuctionAdmin;
